import type { VNode } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useHashRoute } from './routes/useHashRoute';
import {
  parseRoute,
  routePath,
  topTab,
  defaultRouteFor,
  AUDITION_STAGES,
  PROJECT_PHASES,
  CAREER_SECTIONS,
  MATERIALS_SECTIONS,
} from './routes/route';
import { NavZone } from './components/NavZone';
import { SideRail } from './components/SideRail';
import { MasterDetail } from './components/MasterDetail';
import { Strip } from './components/Strip';
import { CaptureSheet } from './components/CaptureSheet';
import { RecordEditorHost } from './components/RecordEditor';
import { GlobalCreate } from './components/GlobalCreate';
import { useBreakpoint, isRail } from './hooks/useBreakpoint';
import { PlaceholderScreen } from './screens/PlaceholderScreen';
import { Today } from './screens/Today';
import { Auditions } from './screens/Auditions';
import { AuditionDetail } from './screens/AuditionDetail';
import { Projects } from './screens/Projects';
import { ProjectDetail } from './screens/ProjectDetail';
import { SourceViewer } from './screens/SourceViewer';
import { Career } from './screens/Career';
import { Materials } from './screens/Materials';
import { Kit } from './screens/Kit';
import { RecordsHarness } from './screens/RecordsHarness';
import { Login } from './screens/Login';
import { TenantResolver } from './screens/TenantResolver';
import { ReviewerPortal } from './screens/ReviewerPortal';
import type { KnotState } from './components/Knot';
import { whoami, verifyMagicLink, type ApiUser, type CaptureContext } from './lib/api';
import { bootstrapTenantFromUrl, getStoredTenantId } from './lib/tenant';

// The literal path a real magic-link email's clicked GET lands on
// (packages/server/src/config.ts's MAGIC_LINK_VERIFY_PATH — same string,
// duplicated here since this package doesn't depend on @vowric/server;
// packages/server/src/plugins/staticWeb.ts's own header documents the
// server-side carve-out that makes GETting this path serve THIS app
// instead of a 404, specifically so this file can read `?token=` off it).
const MAGIC_LINK_VERIFY_PATH = '/auth/magic-link/verify';

type AuthState =
  | { phase: 'booting' }
  | { phase: 'verifying-link' }
  | { phase: 'signed-out'; linkError?: string }
  | { phase: 'signed-in'; user: ApiUser };

function displayName(user: ApiUser): string {
  const trimmed = user.name?.trim();
  if (trimmed) return trimmed;
  const [local] = user.email.split('@');
  return local || user.email;
}

// A2.1b: the top-level auth gate. Unauthenticated -> Login; a just-clicked
// magic-link lands here first (verifying-link) before either Login (link
// invalid/expired) or the authenticated shell; authenticated but no active
// workspace yet -> TenantResolver (Auth-2: GET /auth/tenants -> one
// auto-selects, none -> onboarding, many -> picker); otherwise the normal shell.
//
// B1: the shell is now the 5-tab frame (Today · Auditions · [Knot] ·
// Projects · Career) + the universal Strip pattern (see routes/route.ts
// and components/{Strip,NavZone}.tsx for the route model and the reusable
// component itself). The Knot's capture sheet (CaptureSheet) is reachable
// from every screen here, not per-tab — it's rendered once, at this level.
export function App() {
  const [path, navigate] = useHashRoute();
  const [knotState, setKnotState] = useState<KnotState>('idle');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [auth, setAuth] = useState<AuthState>({ phase: 'booting' });
  const [tenantId, setTenantId] = useState<string | null>(null);
  // The calm, non-blocking "we emailed you a link to confirm your address"
  // note from a fresh password registration — surfaced once, in onboarding.
  const [signupNotice, setSignupNotice] = useState<string | undefined>(undefined);
  // Called unconditionally (rules of hooks) above every early return so the
  // authenticated shell below can pick the bottom-nav vs side-rail rendering.
  const breakpoint = useBreakpoint();

  useEffect(() => {
    bootstrapTenantFromUrl();
    setTenantId(getStoredTenantId());

    async function boot() {
      const isMagicLinkLanding = window.location.pathname === MAGIC_LINK_VERIFY_PATH;
      const token = new URLSearchParams(window.location.search).get('token');

      if (isMagicLinkLanding && token) {
        setAuth({ phase: 'verifying-link' });
        try {
          const { user } = await verifyMagicLink(token);
          window.history.replaceState(null, '', '/');
          setAuth({ phase: 'signed-in', user });
        } catch {
          window.history.replaceState(null, '', '/');
          setAuth({ phase: 'signed-out', linkError: 'This link is invalid or has expired.' });
        }
        return;
      }

      const user = await whoami();
      setAuth(user ? { phase: 'signed-in', user } : { phase: 'signed-out' });
    }

    void boot();
  }, []);

  // B7 — the EXTERNAL reviewer portal. Detected BEFORE the auth/tenant gate:
  // it's for a visitor with NO session and NO tenant, reached by the hash route
  // #/portal/review/:token (the human-facing "send-to-coach" link). The token is
  // the ONLY credential — the portal fetches its own scoped context, tape, and
  // submit against /portal/review/:token (all token-authorized server-side).
  const portalMatch = path.match(/^\/portal\/review\/([^/]+)$/);
  if (portalMatch) {
    return <ReviewerPortal token={decodeURIComponent(portalMatch[1]!)} />;
  }

  // Dev-only design-review route — no shell/nav chrome around it, it's a
  // gallery of the primitives themselves, not a product screen. Available
  // regardless of auth state (it renders no live data).
  if (path === '/_kit') {
    return <Kit />;
  }

  if (auth.phase === 'booting' || auth.phase === 'verifying-link') {
    return (
      <div class="app-shell">
        <PlaceholderScreen
          eyebrow="Vowric"
          line={auth.phase === 'verifying-link' ? 'Signing you in…' : 'One moment…'}
        />
      </div>
    );
  }

  if (auth.phase === 'signed-out') {
    return (
      <div class="app-shell">
        <Login
          initialError={auth.linkError}
          onAuthenticated={(user, opts) => {
            setSignupNotice(
              opts?.justRegistered
                ? 'We emailed you a link to confirm your address — no need to act on it now.'
                : undefined
            );
            setAuth({ phase: 'signed-in', user });
          }}
        />
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div class="app-shell">
        <TenantResolver user={auth.user} notice={signupNotice} onResolved={setTenantId} />
      </div>
    );
  }

  // R2 dev-only harness — proves the schema-driven record-operations engine
  // (create/edit/delete/transition) end to end through the real records API.
  // Rendered here (after auth + tenant are resolved) so it has a live
  // tenantId; no shell/nav chrome (like /_kit). R3 wires RecordForm/
  // RecordActions into the actual product screens.
  if (path === '/_records' || path.startsWith('/_records/')) {
    return (
      <div class="app-shell">
        <RecordsHarness tenantId={tenantId} path={path} navigate={navigate} />
      </div>
    );
  }

  const userName = displayName(auth.user);
  const route = parseRoute(path);
  const activeTab = topTab(route);

  function pressKnot() {
    // R5b: pressing the Knot opens the capture sheet AND starts the Knot
    // listening. The sheet drives the remaining beats (understanding ->
    // proposing -> confirmed) via onKnotState as the capture loop runs; a real
    // microphone amplitude feeding the listening state is a LATER piece (the
    // Knot synthesises a lively amp itself for now).
    setKnotState('listening');
    setSheetOpen(true);
  }

  function closeSheet() {
    setSheetOpen(false);
    setKnotState('idle');
  }

  // R5b: the current page's subject, derived from the route, so a file handed
  // to the Knot while viewing a project/audition pre-associates with it (the
  // backend attaches the retained source to that record). List/other screens
  // carry just the route; the backend falls back to the inbox with no subject.
  const captureContext: CaptureContext =
    route.screen === 'projectDetail'
      ? { route: routePath(route), subjectType: 'actor_project', subjectId: route.id }
      : route.screen === 'auditionDetail'
        ? { route: routePath(route), subjectType: 'actor_audition', subjectId: route.id }
        : { route: routePath(route) };

  let screen;
  let strip;

  switch (route.screen) {
    case 'auditions':
      screen = (
        <Auditions
          tenantId={tenantId}
          stage={route.stage}
          onOpen={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
        />
      );
      strip = (
        <Strip
          items={AUDITION_STAGES}
          activeId={route.stage}
          onSelect={(stage) => navigate(routePath({ screen: 'auditions', stage }))}
        />
      );
      break;
    case 'auditionDetail':
      // No Strip here (NavZoneProps' own doc: omitted entirely, not just
      // empty, for a screen with no sub-view to switch) -- this is a
      // single-record drill-in, same as the blessed demo's aud1 screen,
      // which shows no subnav either. "Back" always lands on the
      // unfiltered Auditions list (routePath's own {stage:'all'} default),
      // same convention as Materials' onBack always landing on Today.
      screen = (
        <AuditionDetail
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(routePath({ screen: 'auditions', stage: 'all' }))}
          onViewSource={(sourceId) =>
            navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))
          }
        />
      );
      strip = undefined;
      break;
    case 'projects':
      screen = (
        <Projects
          tenantId={tenantId}
          phase={route.phase}
          onOpen={(id) => navigate(routePath({ screen: 'projectDetail', id }))}
        />
      );
      strip = (
        <Strip
          items={PROJECT_PHASES}
          activeId={route.phase}
          onSelect={(phase) => navigate(routePath({ screen: 'projects', phase }))}
        />
      );
      break;
    case 'projectDetail':
      // A single-record drill-in — no Strip (same as auditionDetail). Back
      // always lands on the unfiltered Projects list.
      screen = (
        <ProjectDetail
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(routePath({ screen: 'projects', phase: 'all' }))}
          onOpenAudition={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
          onViewSource={(sourceId) =>
            navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))
          }
        />
      );
      strip = undefined;
      break;
    case 'source':
      // The untouched source viewer — back returns to wherever the .srclink
      // was tapped (route.from), mirroring the demo's srcReturn/backFromSrc;
      // a bare source view (no from) falls back to the Projects list.
      screen = (
        <SourceViewer
          tenantId={tenantId}
          id={route.id}
          onBack={() => navigate(route.from ?? routePath({ screen: 'projects', phase: 'all' }))}
        />
      );
      strip = undefined;
      break;
    case 'career':
      screen = (
        <Career
          tenantId={tenantId}
          section={route.section}
          onViewSource={(sourceId) =>
            navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))
          }
          onNavigate={(path) => navigate(path)}
        />
      );
      strip = (
        <Strip
          items={CAREER_SECTIONS}
          activeId={route.section}
          onSelect={(section) => navigate(routePath({ screen: 'career', section }))}
        />
      );
      break;
    case 'materials':
      screen = (
        <Materials
          tenantId={tenantId}
          userName={userName}
          section={route.section}
          onBack={() => navigate(routePath({ screen: 'today' }))}
          onNavigate={(section) => navigate(routePath({ screen: 'materials', section }))}
        />
      );
      strip = (
        <Strip
          items={MATERIALS_SECTIONS}
          activeId={route.section}
          onSelect={(section) => navigate(routePath({ screen: 'materials', section }))}
        />
      );
      break;
    default:
      // Today itself shows the Materials strip too (no chip active — none
      // of "This is me/Headshots/Resume/Reels" represents Today itself);
      // tapping any of them is how the Materials workshop is entered.
      screen = (
        <Today
          tenantId={tenantId}
          userName={userName}
          onAddProject={() => navigate(routePath({ screen: 'projects', phase: 'all' }))}
          onForwardBreakdown={pressKnot}
          onNavigate={(path) => navigate(path)}
        />
      );
      strip = (
        <Strip
          items={MATERIALS_SECTIONS}
          onSelect={(section) => navigate(routePath({ screen: 'materials', section }))}
        />
      );
  }

  // R1a: the bottom nav and the side rail read the SAME nav model (see
  // components/navModel.ts) — only the layout differs by breakpoint.
  const navProps = {
    active: activeTab,
    onNavigate: (tab: typeof activeTab) => navigate(routePath(defaultRouteFor(tab))),
    knotState,
    onKnotPress: pressKnot,
  };
  const captureSheet = (
    <CaptureSheet
      open={sheetOpen}
      name={userName}
      tenantId={tenantId}
      context={captureContext}
      onKnotState={setKnotState}
      onClose={closeSheet}
    />
  );
  // R3 — the manual-CRUD engine's two shell-level pieces: the always-present
  // "+" that opens the global create menu, and the overlay host that renders
  // the create/edit/act forms any screen asks for (see lib/recordEditor.ts).
  const recordsChrome = (
    <>
      <GlobalCreate />
      <RecordEditorHost tenantId={tenantId} />
    </>
  );

  // R1b — TWO-PANE (master-detail) on wide screens for the list→detail flows.
  // Built ONLY under isRail; phone renders the SAME routes full-screen below,
  // byte-identical to R1a. Two flows fit cleanly (both already have a list
  // route + a detail route in the route model): Auditions→AuditionDetail and
  // Projects→ProjectDetail. A bare list route (auditions/projects) shows the
  // list left + a calm empty state right; a detail route shows the parent
  // list left (selection highlighted) + that detail right. The detail screens
  // are reused VERBATIM — onBack de-selects (back to the list route), which
  // is the two-pane analogue of the phone's back-to-list. The Strip stays
  // functioning, pinned above the list pane. Other list screens (Today's
  // stream, Materials galleries, Career's money dashboard — no per-item
  // detail route) stay single-pane centred; masterDetail stays null for them.
  let masterDetail: VNode | null = null;
  if (isRail(breakpoint)) {
    if (route.screen === 'auditions' || route.screen === 'auditionDetail') {
      const selectedId = route.screen === 'auditionDetail' ? route.id : undefined;
      const listStage = route.screen === 'auditions' ? route.stage : 'all';
      masterDetail = (
        <MasterDetail
          strip={
            <Strip
              items={AUDITION_STAGES}
              activeId={route.screen === 'auditions' ? route.stage : undefined}
              onSelect={(stage) => navigate(routePath({ screen: 'auditions', stage }))}
            />
          }
          list={
            <Auditions
              tenantId={tenantId}
              stage={listStage}
              selectedId={selectedId}
              onOpen={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
            />
          }
          detail={
            selectedId ? (
              <AuditionDetail
                tenantId={tenantId}
                id={selectedId}
                onBack={() => navigate(routePath({ screen: 'auditions', stage: listStage }))}
                onViewSource={(sourceId) =>
                  navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))
                }
              />
            ) : (
              <PlaceholderScreen eyebrow="Auditions" line="Select an audition to see its details." />
            )
          }
        />
      );
    } else if (route.screen === 'projects' || route.screen === 'projectDetail') {
      const selectedId = route.screen === 'projectDetail' ? route.id : undefined;
      const listPhase = route.screen === 'projects' ? route.phase : 'all';
      masterDetail = (
        <MasterDetail
          strip={
            <Strip
              items={PROJECT_PHASES}
              activeId={route.screen === 'projects' ? route.phase : undefined}
              onSelect={(phase) => navigate(routePath({ screen: 'projects', phase }))}
            />
          }
          list={
            <Projects
              tenantId={tenantId}
              phase={listPhase}
              selectedId={selectedId}
              onOpen={(id) => navigate(routePath({ screen: 'projectDetail', id }))}
            />
          }
          detail={
            selectedId ? (
              <ProjectDetail
                tenantId={tenantId}
                id={selectedId}
                onBack={() => navigate(routePath({ screen: 'projects', phase: listPhase }))}
                onOpenAudition={(id) => navigate(routePath({ screen: 'auditionDetail', id }))}
                onViewSource={(sourceId) =>
                  navigate(routePath({ screen: 'source', id: sourceId, from: routePath(route) }))
                }
              />
            ) : (
              <PlaceholderScreen eyebrow="Projects" line="Select a project to see its details." />
            )
          }
        />
      );
    }
  }

  // TABLET/DESKTOP: rail on the left. Two-pane list→detail flows fill the row
  // with the MasterDetail (its own panes scroll); every other screen keeps the
  // R1a single-pane treatment — strip promoted into the content header, the
  // content column capped + centred (never left-pinned).
  if (isRail(breakpoint)) {
    return (
      <div class="app-shell app-shell--rail">
        <SideRail {...navProps} />
        {masterDetail ? (
          <div class="app-main">{masterDetail}</div>
        ) : (
          <div class="app-main">
            {strip ? <div class="app-subnav">{strip}</div> : null}
            <main class="app-content">{screen}</main>
          </div>
        )}
        {captureSheet}
        {recordsChrome}
      </div>
    );
  }

  // PHONE: the unchanged single column — screen scrolls inside .app-content,
  // the strip + bottom nav stay pinned at the foot inside the NavZone.
  return (
    <div class="app-shell app-shell--stack">
      <main class="app-content">{screen}</main>
      <NavZone {...navProps} strip={strip} />
      {captureSheet}
      {recordsChrome}
    </div>
  );
}
